import { useEffect, useRef, useState } from 'react';
import { useDisclosure, useOutsideClick } from '../../../hooks';
import {
  Box,
  Button,
  Checkbox,
  Icon,
  IconName,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Paragraph
} from '../../atoms';

export function MultiSelect(props: {
  options: Array<{ value: string; label: string }>;
  values?: string[];
  onValuesChange?: (values: string[]) => void;
  clearLabel?: string;
  saveLabel?: string;
  multiValuesLabel: string;
  noValuesLabel: string;
}): JSX.Element {
  const {
    options,
    values = [],
    onValuesChange,
    clearLabel,
    saveLabel,
    multiValuesLabel,
    noValuesLabel
  } = props;
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentValues, setCurrentValues] = useState<
    Array<{ value: string; label: string }>
  >([]);

  const handleOptionClick = (value: string): void => {
    if (selectedValues.includes(value)) {
      setSelectedValues((currentValues) =>
        currentValues.filter((val) => val !== value)
      );
      return;
    }

    setSelectedValues((currentValues) => [...currentValues, value]);
  };

  useEffect(() => {
    if (values.length === 0) {
      return;
    }

    const newValues = values
      .map((value) => options.find((opt) => opt.value === value))
      .filter(Boolean) as Array<{ value: string; label: string }>;

    setCurrentValues(newValues);
    setSelectedValues(values);
  }, [values, setCurrentValues, setSelectedValues, options]);

  const handleSaveClick = (): void => {
    onValuesChange?.(selectedValues);

    const newValues = selectedValues
      .map((value) => options.find((opt) => opt.value === value))
      .filter(Boolean) as Array<{ value: string; label: string }>;
    setCurrentValues(newValues);

    onClose();
  };

  const handleClearClick = (): void => {
    setSelectedValues([]);
    setCurrentValues([]);
    onValuesChange?.([]);
  };

  const menuListRef = useRef<HTMLDivElement>(null);

  useOutsideClick({ ref: menuListRef, handler: () => onClose() });

  return (
    <Menu isOpen={isOpen}>
      <Input
        type="button"
        as={MenuButton}
        onClick={(): void => onOpen()}
        sx={{
          ...(currentValues.length
            ? {
                backgroundColor: 'palette_blue.100',
                color: 'white'
              }
            : {}),
          minWidth: '164px'
        }}
      >
        <Paragraph>
          {getMenuValues({
            multiValuesLabel,
            noValuesLabel,
            values: currentValues
          })}
          <Icon
            name={IconName.ArrowDown}
            width="12px"
            height="12px"
            marginLeft={2}
          />
        </Paragraph>
      </Input>
      <MenuList ref={menuListRef}>
        {options.map((option) => (
          <MenuItem
            key={option.value}
            onClick={(e): void => {
              e.stopPropagation();
              e.preventDefault();
              handleOptionClick(option.value);
            }}
          >
            <Checkbox
              onChange={(): void => {
                handleOptionClick(option.value);
              }}
              isChecked={selectedValues.includes(option.value)}
              style={{
                width: '100%'
              }}
            >
              {option.label}
            </Checkbox>
          </MenuItem>
        ))}
        <Box
          display="flex"
          justifyContent="center"
          gap={4}
          marginTop={2}
          px={2}
        >
          <Button type="button" variant="secondary" onClick={handleClearClick}>
            {clearLabel}
          </Button>
          <Button type="button" onClick={handleSaveClick}>
            {saveLabel}
          </Button>
        </Box>
      </MenuList>
    </Menu>
  );
}

const getMenuValues = ({
  noValuesLabel,
  multiValuesLabel,
  values
}: {
  noValuesLabel: string;
  multiValuesLabel: string;
  values: Array<{ value: string; label: string }>;
}): string => {
  if (!values.length) {
    return noValuesLabel;
  }

  if (values.length === 1) {
    return values[0].label.length >= 12
      ? `${values[0].label.slice(0, 12)}...`
      : values[0].label;
  }

  return multiValuesLabel;
};
